import type { FetchBaseQueryError } from "@reduxjs/toolkit/query"

/** Above this recipient count we require explicit confirmation before launch. */
export const RECIPIENTS_WARNING_THRESHOLD = 20000

/** Array-valued filter keys that constitute a real audience restriction. */
export const FILTER_KEYS = [
  "cities",
  "agents",
  "campaigns",
  "bot_type_ids",
] as const

/**
 * True when at least one audience-restricting filter is active. `limit` is NOT
 * a filter — it only caps an otherwise unrestricted query, so it is ignored.
 */
export const hasActiveFilters = (
  filters: Record<string, unknown> | undefined | null,
): boolean =>
  !!filters &&
  FILTER_KEYS.some((key) => {
    const value = filters[key]
    return Array.isArray(value) && value.length > 0
  })

const isFetchBaseQueryError = (e: unknown): e is FetchBaseQueryError =>
  typeof e === "object" && e !== null && "status" in e

/** Render a clear, operator-facing message for a failed user query. */
export const formatUsersQueryError = (error: unknown): string => {
  if (isFetchBaseQueryError(error)) {
    return `Не удалось загрузить пользователей по фильтрам (ошибка ${error.status}). Список получателей НЕ обновлён. Проверьте фильтры и попробуйте снова.`
  }
  return "Не удалось загрузить пользователей по фильтрам. Список получателей НЕ обновлён. Проверьте фильтры и попробуйте снова."
}

export interface LaunchWarningInput {
  hasFilters: boolean
  allUsersOptIn: boolean
  recipientCount: number
}

/**
 * Returns warning lines that must be confirmed before launching a mailing.
 * Empty array = safe to launch without extra confirmation.
 */
export const getLaunchWarnings = ({
  hasFilters,
  allUsersOptIn,
  recipientCount,
}: LaunchWarningInput): string[] => {
  const warnings: string[] = []
  if (!hasFilters && !allUsersOptIn) {
    warnings.push(
      "Рассылка запускается без фильтров — сообщение получат все пользователи.",
    )
  }
  if (recipientCount > RECIPIENTS_WARNING_THRESHOLD) {
    warnings.push(`Количество получателей ${recipientCount}, вы уверены?`)
  }
  return warnings
}
